import React, { useEffect, useState } from 'react';
import { BarChart3, Users, BookOpen, TrendingUp, Clock, UserCheck, UserX, Calendar } from 'lucide-react';
import { DashboardStats } from '../types';
import { getDashboardStats, getMonthlyAttendanceData } from '../services/dashboardService';
import { useAuth } from '../context/AuthContext';
import { databases } from '../services/appwriteClient';
import { Query } from 'appwrite';

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  trend: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, trend }) => {
  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-2xl font-semibold mt-1">{value}</p>
          <p className="text-sm text-gray-500 mt-1">{trend}</p>
        </div>
        <div className="p-3 bg-gray-50 rounded-full">
          {icon}
        </div>
      </div>
    </div>
  );
};

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [monthlyAttendance, setMonthlyAttendance] = useState<Array<{ date: string; attendance: number }>>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState<'7days' | '30days' | 'month'>('7days');
  const { user } = useAuth();
  
  // Add calendar state
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const [dashboardStats, attendanceData] = await Promise.all([
          getDashboardStats(),
          getMonthlyAttendanceData(selectedPeriod)
        ]);
        setStats(dashboardStats);
        setMonthlyAttendance(attendanceData);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchDashboardData();
  }, [selectedPeriod]);
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Calendar helper functions
  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const handlePrevMonth = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() - 1);
    setCurrentDate(newDate);
  };

  const handleNextMonth = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + 1);
    setCurrentDate(newDate);
  };

  const handleDateClick = (day: number) => {
    const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    setSelectedDate(newDate);
  };

  // Generate calendar days
  const renderCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];
    const today = new Date();

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(
        <div key={`empty-${i}`} className="h-12 w-12 flex items-center justify-center">
          <span className="text-gray-300">·</span>
        </div>
      );
    }

    // Add cells for each day of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const isSelected = selectedDate && 
        selectedDate.getDate() === day && 
        selectedDate.getMonth() === currentDate.getMonth() && 
        selectedDate.getFullYear() === currentDate.getFullYear();
      
      const isToday = today.getDate() === day && 
        today.getMonth() === currentDate.getMonth() && 
        today.getFullYear() === currentDate.getFullYear();

      days.push(
        <button
          key={day}
          onClick={() => handleDateClick(day)}
          className={`h-12 w-12 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-200
            ${isSelected 
              ? 'bg-pink-600 text-white shadow-md scale-105' 
              : isToday
                ? 'bg-pink-50 text-pink-700 border-2 border-blue-500 hover:bg-pink-100'
                : 'hover:bg-gray-100 text-gray-700'
            }`}
        >
          {day}
        </button>
      );
    }

    return days;
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <StatCard 
              title="Total Students" 
              value={stats?.totalStudents || 0} 
              icon={<Users className="text-pink-500" />} 
              trend="+5% from last month"
            />
            <StatCard 
              title="Total Teachers" 
              value={stats?.totalTeachers || 0} 
              icon={<BookOpen className="text-green-500" />} 
              trend="No change"
            />
            <StatCard 
              title="Attendance Rate" 
              value={`${stats?.attendanceRate || 0}%`} 
              icon={<BarChart3 className="text-purple-500" />} 
              trend="+2% from last week"
            />
          </div>
          
          {/* Today's Attendance Summary */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold flex items-center">
                <Clock className="mr-2 text-pink-500" />
                Today's Attendance Summary
              </h2>
              <p className="text-sm text-gray-500">{new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-green-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-600">Present</p>
                    <p className="text-2xl font-semibold text-green-700">{stats?.todayAttendance.present || 0}</p>
                  </div>
                  <div className="p-3 bg-green-100 rounded-full">
                    <UserCheck className="text-green-600" size={24} />
                  </div>
                </div>
                <p className="text-sm text-green-600 mt-2">
                  {stats?.todayAttendance.present 
                    ? `${Math.round((stats.todayAttendance.present / stats.todayAttendance.total) * 100)}%`
                    : '0%'} of total
                </p>
              </div>

              <div className="bg-red-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-red-600">Absent</p>
                    <p className="text-2xl font-semibold text-red-700">{stats?.todayAttendance.absent || 0}</p>
                  </div>
                  <div className="p-3 bg-red-100 rounded-full">
                    <UserX className="text-red-600" size={24} />
                  </div>
                </div>
                <p className="text-sm text-red-600 mt-2">
                  {stats?.todayAttendance.absent
                    ? `${Math.round((stats.todayAttendance.absent / stats.todayAttendance.total) * 100)}%`
                    : '0%'} of total
                </p>
              </div>

              <div className="bg-yellow-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-yellow-600">Late</p>
                    <p className="text-2xl font-semibold text-yellow-700">{stats?.todayAttendance.late || 0}</p>
                  </div>
                  <div className="p-3 bg-yellow-100 rounded-full">
                    <Clock className="text-yellow-600" size={24} />
                  </div>
                </div>
                <p className="text-sm text-yellow-600 mt-2">
                  {stats?.todayAttendance.late
                    ? `${Math.round((stats.todayAttendance.late / stats.todayAttendance.total) * 100)}%`
                    : '0%'} of total
                </p>
              </div>
            </div>
          </div>

          {/* Calendar Section */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                <Calendar className="mr-2 text-pink-500" size={24} />
                Calendar
              </h2>
              <div className="flex items-center gap-3">
                <button
                  onClick={handlePrevMonth}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-600 hover:text-gray-900"
                  aria-label="Previous month"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
                <span className="text-lg font-medium text-gray-700 min-w-[200px] text-center">
                  {formatDate(currentDate)}
                </span>
                <button
                  onClick={handleNextMonth}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-600 hover:text-gray-900"
                  aria-label="Next month"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="grid grid-cols-7 gap-1 mb-3">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                  {day}
                </div>
              ))}
            </div>
            
            <div className="grid grid-cols-7 gap-1">
              {renderCalendarDays()}
            </div>

            {selectedDate && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-sm text-gray-600">
                  Selected date: {selectedDate.toLocaleDateString('en-US', { 
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>
            )}
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-lg font-semibold flex items-center">
                  <BarChart3 className="mr-2 text-pink-500" />
                  Attendance Overview
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {selectedPeriod === '7days' ? 'Last 7 days' : 
                   selectedPeriod === '30days' ? 'Last 30 days' : 
                   'This month'} attendance trend
                </p>
              </div>
              <select 
                className="text-sm border border-gray-300 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value as '7days' | '30days' | 'month')}
              >
                <option value="7days">Last 7 days</option>
                <option value="30days">Last 30 days</option>
                <option value="month">This month</option>
              </select>
            </div>

            <div className="h-80 relative">
              {/* Y-axis labels */}
              <div className="absolute left-0 top-0 bottom-0 w-12 flex flex-col justify-between text-xs text-gray-500">
                <div>100%</div>
                <div>75%</div>
                <div>50%</div>
                <div>25%</div>
                <div>0%</div>
              </div>

              {/* Grid lines */}
              <div className="absolute left-12 right-0 top-0 bottom-0 flex flex-col justify-between">
                {[0, 25, 50, 75, 100].map((percentage) => (
                  <div key={percentage} className="w-full border-t border-gray-100"></div>
                ))}
              </div>

              {monthlyAttendance.length > 0 ? (
                <div className="absolute left-12 right-0 top-0 bottom-0 flex items-end space-x-2 px-2 h-full">
                  {monthlyAttendance.map((record, index) => {
                    const prevAttendance = index > 0 ? monthlyAttendance[index - 1].attendance : null;
                    const height = record.attendance;
                    const color = height >= 80 ? 'bg-green-500' :
                                height >= 60 ? 'bg-pink-500' :
                                height >= 40 ? 'bg-yellow-500' :
                                'bg-red-500';

                    return (
                      <div key={record.date} className="flex-1 flex flex-col items-center group h-full justify-end">
                        <div 
                          className={`${color} w-full rounded-t transition-all duration-300 hover:opacity-80`}
                          style={{
                            height: `${height}%`,
                            minHeight: height === 0 ? '2px' : undefined
                          }}
                        >
                          {/* Tooltip */}
                          <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white px-3 py-2 rounded text-sm opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap shadow-lg z-10">
                            <div className="font-medium">{formatDate(new Date(record.date))}</div>
                            <div className="text-xs mt-1">
                              {height}% attendance
                              {height >= 80 ? ' (Excellent)' :
                               height >= 60 ? ' (Good)' :
                               height >= 40 ? ' (Fair)' :
                               ' (Needs Improvement)'}
                            </div>
                            {prevAttendance !== null && (
                              <div className={`text-xs mt-1 ${height > prevAttendance ? 'text-green-400' : height < prevAttendance ? 'text-red-400' : 'text-gray-400'}`}>
                                {height > prevAttendance ? '↑' : height < prevAttendance ? '↓' : '→'} {Math.abs(height - prevAttendance)}% from previous day
                              </div>
                            )}
                          </div>
                        </div>
                        <span className="text-xs text-gray-600 mt-2 font-medium">
                          {formatDate(new Date(record.date))}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="absolute left-12 right-0 top-0 bottom-0 flex items-center justify-center text-gray-500">
                  No attendance data available
                </div>
              )}
            </div>

            <div className="mt-6 flex flex-wrap justify-center gap-4">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                <span className="text-sm text-gray-600">Excellent (80-100%)</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-pink-500 rounded-full mr-2"></div>
                <span className="text-sm text-gray-600">Good (60-79%)</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-yellow-500 rounded-full mr-2"></div>
                <span className="text-sm text-gray-600">Fair (40-59%)</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
                <span className="text-sm text-gray-600">Needs Improvement (0-39%)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;